import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import EbookCard from "../components/ebook/EbookCard";
import EbookDownloadButton from "../components/ebook/EbookDownloadButton";
import { fetchEbookDetail, formatFileSize } from "../api/ebookApi";
import { assetUrl } from "../utils/api";

export default function EbookDetail() {
  const { ebookId } = useParams();
  const userId = localStorage.getItem("user_id") || "";
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const pagePath = `/ebooks/${ebookId}`;

  useSeo(
    item
      ? {
          title: `${item.title} - 会员电子书`,
          metaDescription: (item.description || `${item.title}，会员免费下载`).slice(0, 120),
          ogDescription: item.description || item.title,
          keywords: "电子书,婚恋阅读,心理成长",
          path: pagePath,
          ogImage: item.cover_path || "",
        }
      : { title: "电子书详情", path: pagePath, noindex: true }
  );

  useEffect(() => {
    setLoading(true);
    fetchEbookDetail(ebookId, userId)
      .then((data) => {
        setItem(data.item || null);
        setRelated(data.related || []);
        setError("");
      })
      .catch((err) => {
        setItem(null);
        setRelated([]);
        setError(err.message || "电子书不存在");
      })
      .finally(() => setLoading(false));
  }, [ebookId, userId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="bg-white p-12 rounded-2xl shadow text-center text-gray-400">
              加载中...
            </div>
          ) : error || !item ? (
            <div className="bg-white p-12 rounded-2xl shadow text-center space-y-4">
              <p className="text-gray-500">{error || "电子书不存在或已下架"}</p>
              <Link to="/book-club?tab=ebooks" className="text-pink-600 hover:underline">
                返回读书会 · 电子书
              </Link>
            </div>
          ) : (
            <article className="space-y-8">
              <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                <div className="grid md:grid-cols-[240px_1fr] gap-0">
                  {item.cover_path ? (
                    <img
                      src={assetUrl(item.cover_path)}
                      alt={item.title}
                      className="w-full h-72 md:h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-72 md:h-full bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center text-6xl">
                      📚
                    </div>
                  )}

                  <div className="p-7 md:p-10 space-y-5">
                    <Link to="/book-club?tab=ebooks" className="text-sm text-pink-500 hover:underline">
                      ← 返回读书会 · 电子书
                    </Link>
                    <div>
                      <p className="text-sm text-pink-500 mb-1">{item.category_name || "未分类"}</p>
                      <h1 className="text-3xl font-bold text-gray-800">{item.title}</h1>
                      <p className="text-gray-600 mt-2">作者：{item.author || "未知"}</p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-600 bg-rose-50 rounded-2xl p-5">
                      <p>出版社：{item.publisher || "—"}</p>
                      <p>出版时间：{item.publish_year || "—"}</p>
                      <p>文件格式：{item.file_type ? String(item.file_type).toUpperCase() : "—"}</p>
                      <p>文件大小：{formatFileSize(item.file_size) || "—"}</p>
                      <p className="sm:col-span-2">分类：{item.category_name || "—"}</p>
                    </div>

                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5 space-y-3">
                      <p className="text-sm text-emerald-700 font-medium">✓ 注册会员免费下载</p>
                      <EbookDownloadButton ebook={item} userId={userId} />
                    </div>
                  </div>
                </div>

                {item.description ? (
                  <section className="px-7 md:px-10 pb-10 border-t border-rose-50 pt-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">简介</h2>
                    <p className="text-gray-700 whitespace-pre-line leading-8">{item.description}</p>
                  </section>
                ) : null}
              </div>

              {related.length > 0 ? (
                <section className="space-y-4">
                  <h2 className="text-2xl font-bold text-gray-800">📖 你可能还喜欢</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {related.map((book) => (
                      <EbookCard key={book.id} ebook={book} userId={userId} compact />
                    ))}
                  </div>
                </section>
              ) : null}
            </article>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
